import { Autocomplete, ListItemIcon, Stack, TextField } from '@mui/material'
import AppBar from '@mui/material/AppBar'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Toolbar from '@mui/material/Toolbar'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import Logo from 'app/components/Logo'
import { globalConstants } from 'app/constants/globalConstants'
import * as React from 'react'
import { NavLink } from 'react-router-dom'
import { theme } from 'app/theme/theme'
import { useMsal } from '@azure/msal-react'
import Logout from '@mui/icons-material/Logout'
import { useAppService } from 'app/services/main/AppService'
import { useGetToken } from 'app/services/hooks/useGetToken'

interface HeaderProps {
  onHelpClick?: () => void
}

const getInitials = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

const avatarColors = [
  '#1565C0',
  '#2E7D32',
  '#6A1B9A',
  '#AD1457',
  '#EF6C00',
  '#00838F',
  '#5D4037',
]

const getAvatarColor = (name: string) => {
  const colorIndex = name
    .split('')
    .reduce((total, character) => total + character.charCodeAt(0), 0)

  return avatarColors[colorIndex % avatarColors.length]
}

export function Header({ onHelpClick }: HeaderProps) {
  const {
    currentDomainDetails,
    domains,
    setCurrentDomainDetails,
    getPages,
    user,
  } = useAppService()

  const displayName = user?.username?.trim() || user?.email?.trim() || 'User'
  const initials = getInitials(displayName)
  const avatarColor = getAvatarColor(displayName)
  const profileImageUrl = user?.profileImageUrl || user?.profile_image_url
  const getToken = useGetToken()
  const [profileImageSrc, setProfileImageSrc] = React.useState<string>()

  React.useEffect(() => {
    let objectUrl: string | undefined
    let cancelled = false

    setProfileImageSrc(undefined)

    if (!profileImageUrl) {
      return undefined
    }

    const loadProfileImage = async () => {
      const accessToken = await getToken()
      if (!accessToken) {
        return
      }

      const response = await fetch(profileImageUrl, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })

      if (!response.ok) {
        return
      }

      objectUrl = URL.createObjectURL(await response.blob())
      if (cancelled) {
        URL.revokeObjectURL(objectUrl)
        return
      }

      setProfileImageSrc(objectUrl)
    }

    loadProfileImage().catch(() => undefined)

    return () => {
      cancelled = true
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl)
      }
    }
  }, [getToken, profileImageUrl])

  const { instance } = useMsal()

  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(
    null
  )

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget)
  }

  const handleCloseUserMenu = () => {
    setAnchorElUser(null)
  }

  const handleLogout = () => {
    instance.logoutRedirect()
  }

  return (
    <AppBar position='fixed' color='secondary'>
      <Container maxWidth='xl' disableGutters>
        <Toolbar
          variant='dense'
          sx={{
            justifyContent: 'space-between',
            paddingTop: '8px',
            paddingBottom: '8px',
          }}
        >
          <Stack direction='row' sx={{ height: '28px' }}>
            <Logo
              navigateTo={globalConstants.homePath}
              className='h-[50px] mr-[28px] self-center cursor-pointer '
              logo='/assets/royalCaninn.jpg'
            />
            <div className='bg-[#efefef] w-[2px]' />
            <Typography
              sx={{
                fontWeight: '600',
                marginLeft: '25px',
                alignSelf: 'center',
                fontSize: '16px',
              }}
            >
              Upload Hub
            </Typography>
          </Stack>
          <Stack direction='row' sx={{ height: '28px' }}>
            {domains && currentDomainDetails && setCurrentDomainDetails && (
              <Autocomplete
                options={domains}
                value={currentDomainDetails}
                getOptionLabel={(option) => option.domain}
                onChange={(_, value) => {
                  setCurrentDomainDetails(value)
                }}
                isOptionEqualToValue={(option, value) =>
                  option.domainDivisionRegionId === value.domainDivisionRegionId
                }
                disableClearable
                sx={{
                  fontSize: '12px',
                  '& ::before': {
                    borderBottom: 'none !important',
                  },
                  '&.MuiAutocomplete-root .MuiAutocomplete-inputRoot .MuiAutocomplete-input':
                    {
                      textAlign: 'end !important',
                      width: 'auto',
                    },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label=''
                    variant='standard'
                    size='small'
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                )}
              />
            )}
            <div className='bg-[#efefef] w-[2px]' />

            <Box sx={{ padding: '0 25px 0 25px' }}>
              <Tooltip title='Help'>
                <IconButton
                  id='help-icon-button'
                  onClick={onHelpClick}
                  sx={{ p: 0 }}
                >
                  <Typography
                    sx={{
                      fontWeight: '600',
                      alignSelf: 'center',
                      textDecoration: 'none',
                      boxShadow: 'none',
                      color: 'inherit',
                    }}
                  >
                    ?
                  </Typography>
                </IconButton>
              </Tooltip>
            </Box>

            <div className='bg-[#efefef] w-[2px]' />

            <Box sx={{ padding: '0 25px 0 25px' }}>
              <IconButton
                id='profile-icon-button'
                aria-controls={anchorElUser ? 'menu-appbar' : undefined}
                aria-haspopup='true'
                aria-expanded={anchorElUser ? 'true' : undefined}
                onClick={handleOpenUserMenu}
                sx={{ p: 0 }}
              >
                <Avatar
                  alt={displayName}
                  src={profileImageSrc}
                  sx={{
                    width: 24,
                    height: 24,
                    backgroundColor: avatarColor,
                  }}
                >
                  <Box
                    component='span'
                    sx={{
                      display: 'flex',
                      width: '100%',
                      height: '100%',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxSizing: 'border-box',
                      padding: '2px',
                      fontSize: '12px',
                      lineHeight: 1,
                    }}
                  >
                    {initials}
                  </Box>
                </Avatar>
                <Typography
                  sx={{
                    marginLeft: '8px',
                    whiteSpace: 'nowrap',
                    fontSize: '14px',
                  }}
                >
                  {displayName}
                </Typography>
              </IconButton>
              <Menu
                id='menu-appbar'
                aria-labelledby='profile-icon-button'
                anchorEl={anchorElUser}
                keepMounted
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                slotProps={{
                  paper: {
                    elevation: 0,
                    sx: {
                      overflow: 'visible',
                      filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                      mt: 1.5,
                      ml: 1,
                      '& .MuiAvatar-root': {
                        width: 32,
                        height: 32,
                      },
                      '&:before': {
                        content: '""',
                        display: 'block',
                        position: 'absolute',
                        top: 0,
                        right: 14,
                        width: 10,
                        height: 10,
                        bgcolor: 'background.paper',
                        transform: 'translateY(-50%) rotate(45deg)',
                        zIndex: 0,
                      },
                    },
                  },
                }}
              >
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon>
                    <Logout fontSize='small' />
                  </ListItemIcon>
                  Logout
                </MenuItem>

                {/* <MenuItem onClick={()=>{navigate('/access-management')}}>
                  <ListItemIcon>
                    <PeopleAltIcon fontSize='small' />
                  </ListItemIcon>

                  Access Management
                </MenuItem> */}
              </Menu>
            </Box>
          </Stack>
        </Toolbar>
        <Toolbar
          variant='dense'
          sx={{
            minHeight: '40px',
            backgroundColor: '#0000A0',
            paddingTop: 0,
            paddingBottom: 0,
            gap: '20px',
            lineHeight: '2.6',
            fontSize: '14px',
          }}
        >
          {getPages &&
            getPages.map((item) => (
              <NavLink
                key={item.key}
                to={item.route}
                className='px-4'
                style={({ isActive }) =>
                  isActive
                    ? {
                        textDecoration: 'none',
                        color: theme.palette.secondary.main,
                        fontWeight: 600,
                        borderBottomStyle: 'solid',
                      }
                    : {
                        textDecoration: 'none',
                        color: theme.palette.secondary.main,
                      }
                }
              >
                {item.label}
              </NavLink>
            ))}
        </Toolbar>
      </Container>
    </AppBar>
  )
}

Header.defaultProps = {
  onHelpClick: undefined,
}
